import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { UserStats } from '../../app/types';
import { type AccountRole, getAccountRoleLabel, AccountSwitcherDropdown } from '../../accounts';
import { getLearnerNavItems } from './navigationHelpers';
import { APP_NAME, getProgressToNextLevel } from '../../config/constants';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { useAppStore } from '../../app/hooks/useAppStore';
interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  stats: UserStats;
  displayName: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  activeAccountRole?: string;
}
export function Sidebar({
  currentView,
  onNavigate,
  stats,
  displayName,
  isCollapsed,
  onToggleCollapse,
  activeAccountRole
}: SidebarProps) {
  const {
    accounts,
    viewingLearnerId,
    setViewingLearner
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const isTeacherOrParent = activeAccountRole === 'teacher' || activeAccountRole === 'parent' || accounts && accounts.activeAccountId && accounts.accountsById[accounts.activeAccountId]?.role !== 'learner';
  // Get role-based navigation items — use actual role, not just teacher/learner
  const effectiveRole = activeAccountRole || accounts && accounts.activeAccountId && accounts.accountsById[accounts.activeAccountId]?.role || 'learner';
  const navItems = getLearnerNavItems();
  // Calculate XP progress for the profile section (safe access)
  const progressToNextLevel = stats?.xp != null ? getProgressToNextLevel(stats.xp).percent : 0;
  // Streak
  const streak = stats?.xp != null ? accounts?.accountsById[accounts?.activeAccountId || '']?.appState?.streak?.currentTotalStreak || 0 : 0;
  return <motion.aside className="hidden md:flex flex-col h-full bg-white border-r-2 border-slate-100 sticky top-0 z-30 shadow-[4px_0_24px_rgba(249,115,22,0.03)]" initial={false} animate={{
    width: isCollapsed ? 88 : 280
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30
  }} data-id="element-1125">
      {/* Logo Section - Playful */}
      <div className="h-24 flex items-center px-7 relative overflow-hidden" data-id="element-1126">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none" data-id="element-1127"></div>

        {/* @djay-slot: sidebar-logo */}
        <motion.div className="flex-shrink-0 relative z-10" whileHover={{
        rotate: 10,
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-1128">
          <DjayAvatar size="lg" emotion="happy" variant={selectedVariant} data-id="element-1129" />
        </motion.div>

        <AnimatePresence data-id="element-1130">
          {!isCollapsed && <motion.span initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} className="font-black text-slate-800 ml-4 text-2xl tracking-tight whitespace-nowrap overflow-hidden relative z-10" data-id="element-1131">
              {APP_NAME}
            </motion.span>}
        </AnimatePresence>
      </div>

      {/* Navigation Items - Chunky & Colorful */}
      <nav className="flex-1 py-2 px-4 space-y-2 overflow-y-auto" data-id="element-1132">
        {navItems.map(item => {
        const isActive = currentView === item.id;
        // Assign fun colors to different menu items
        let activeColor = 'text-orange-600 bg-orange-50 border-orange-200 shadow-sm';
        let iconColor = isActive ? 'text-orange-600' : 'text-slate-400';
        if (isActive) {
          if (item.id === 'home') {
            activeColor = 'text-blue-600 bg-blue-50 border-blue-200 shadow-sm';
            iconColor = 'text-blue-600';
          }
          if (item.id === 'modules') {
            activeColor = 'text-emerald-600 bg-emerald-50 border-emerald-200 shadow-sm';
            iconColor = 'text-emerald-600';
          }
          if (item.id === 'progress') {
            activeColor = 'text-amber-600 bg-amber-50 border-amber-200 shadow-sm';
            iconColor = 'text-amber-600';
          }
          if (item.id === 'assessment') {
            activeColor = 'text-orange-600 bg-orange-50 border-orange-200 shadow-sm';
            iconColor = 'text-orange-600';
          }
          if (item.id === 'learningLab') {
            activeColor = 'text-purple-600 bg-purple-50 border-purple-200 shadow-sm';
            iconColor = 'text-purple-600';
          }
        }
        return <button key={item.id} onClick={() => onNavigate(item.id)} className={`
                w-full flex items-center px-4 py-3.5 rounded-2xl transition-all duration-200 group relative overflow-hidden border-2
                ${isActive ? activeColor : 'border-transparent text-slate-600 hover:bg-slate-50 hover:border-slate-200 hover:text-slate-900'}
                ${item.isDev ? 'mt-8 border-t-2 border-slate-100 pt-6' : ''}
              `} title={isCollapsed ? item.label : undefined} data-id="element-1133">
              <item.icon className={`w-6 h-6 flex-shrink-0 relative z-10 transition-transform duration-300 ${iconColor} ${isActive ? 'animate-wiggle' : 'group-hover:scale-110'}`} strokeWidth={isActive ? 2.5 : 2} data-id="element-1134" />

              <AnimatePresence data-id="element-1135">
                {!isCollapsed && <motion.span initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} className={`ml-4 whitespace-nowrap overflow-hidden relative z-10 text-base ${isActive ? 'font-black' : 'font-bold'}`} data-id="element-1136">
                    {item.label}
                  </motion.span>}
              </AnimatePresence>
            </button>;
      })}
      </nav>

      {/* User Profile Section */}
      {isTeacherOrParent && !isCollapsed && <div className="p-4 flex items-center gap-3 mb-4 border-t border-slate-100" data-id="element-1137">
          <motion.div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-200 flex-shrink-0" whileHover={{
        scale: 1.05,
        rotate: 5
      }} whileTap={{
        scale: 0.95
      }} data-id="element-1138">
            <DjayAvatar size="lg" emotion="happy" variant={selectedVariant} data-id="element-1139" />
          </motion.div>

          <div className="flex-1 min-w-0" data-id="element-1140">
            <div className="font-black text-slate-800 truncate text-base mb-0.5" data-id="element-1141">
              {displayName ?? ''}
            </div>
            <div className="flex items-center gap-2" data-id="element-1142">
              <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md border border-orange-100" data-id="element-1143">
                {getAccountRoleLabel(effectiveRole as AccountRole || 'learner')}
              </span>
            </div>
          </div>
        </div>}

      {/* Collapse Toggle */}
      <button onClick={onToggleCollapse} className="absolute -right-3.5 top-28 w-7 h-7 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center shadow-sm text-slate-400 hover:text-orange-500 hover:border-orange-300 hover:scale-110 transition-all z-40 focus:outline-none" aria-label={isCollapsed ? 'Uitklappen' : 'Inklappen'} data-id="element-1144">
        {isCollapsed ? <ChevronRight className="w-4 h-4 stroke-[3]" data-id="element-1145" /> : <ChevronLeft className="w-4 h-4 stroke-[3]" data-id="element-1146" />}
      </button>
    </motion.aside>;
}